import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogFooter } from './Dialog';
import { Button } from './Button';

/**
 * In-app confirmation dialog. Replaces the browser confirm dialog. Awaitable via the
 * `useConfirm()` hook — controllers can `await confirm({title, body})` and
 * get a boolean back without manually wiring up state.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Delete vendor?', body: '...' });
 *   if (!ok) return;
 *   await api.delete(...)
 */

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export function ConfirmDialog({
  open,
  options,
  onResolve,
}: {
  open: boolean;
  options: ConfirmOptions | null;
  onResolve: (ok: boolean) => void;
}) {
  const { t } = useTranslation();
  if (!options) return null;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next) onResolve(false); }}
      title={options.title}
      description={options.body}
    >
      <DialogFooter>
        <Button
          variant={options.destructive ? 'danger' : 'primary'}
          onClick={() => onResolve(true)}
          autoFocus
        >
          {options.confirmLabel ?? t('common.confirm')}
        </Button>
        <Button variant="ghost" onClick={() => onResolve(false)}>
          {options.cancelLabel ?? t('common.cancel')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// Awaitable provider/hook ─────────────────────────────────────────────
import { createContext, useCallback, useContext, useRef, ReactNode } from 'react';

type Resolver = (ok: boolean) => void;

const ConfirmCtx = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<Resolver | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOptions(o);
    setOpen(true);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const onResolve = (ok: boolean) => {
    setOpen(false);
    const r = resolver.current;
    resolver.current = null;
    r?.(ok);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <ConfirmDialog open={open} options={options} onResolve={onResolve} />
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm() must be used inside <ConfirmProvider>');
  return ctx;
}
